import { useMemo } from 'react';
import { Report } from './types';
import {
  getCompletenessRating,
  getRiskLevel,
  hasEvaluationData,
  RISK_SCALE_MAX,
} from './scoring';
import { useAnimatedNumber } from './useAnimatedNumber';

/**
 * "Security Summary Assessment Results" charts (completeness radial ring + risk
 * gauge) + the 4 gap/issue metric cards, driven by real evaluation data
 * from the currently selected report. Ported from
 * dummy_webpage/current/view2.js's createCompletenessChart()/
 * createRiskGauge()/renderMetricCard(), converted from imperative DOM
 * manipulation to React state/refs (useAnimatedNumber drives the same
 * ease-out sweep the original requestAnimationFrame driver did).
 *
 * Renders an empty-state message when there is no report selected or the
 * selected report has no completed evaluation results yet (see
 * hasEvaluationData()) — mirrors renderSecuritySummaryAssessmentResults()'s early-return
 * branches in the original prototype.
 */

// Gauge geometry — a near-complete ring with a small gap at the bottom.
const GAUGE_CENTER = 85;
const GAUGE_RADIUS = 68;
const GAUGE_GAP_DEG = 40;
const GAUGE_START_ANGLE = 90 + GAUGE_GAP_DEG / 2;
const GAUGE_SWEEP_DEG = 360 - GAUGE_GAP_DEG;

function pointOnGauge(angleDeg: number, radius: number = GAUGE_RADIUS) {
  const rad = (angleDeg * Math.PI) / 180;
  return {
    x: GAUGE_CENTER + radius * Math.cos(rad),
    y: GAUGE_CENTER + radius * Math.sin(rad),
  };
}

function gaugeArcPath(startDeg: number, endDeg: number): string {
  const start = pointOnGauge(startDeg);
  const end = pointOnGauge(endDeg);
  const largeArc = endDeg - startDeg > 180 ? 1 : 0;
  return `M ${start.x} ${start.y} A ${GAUGE_RADIUS} ${GAUGE_RADIUS} 0 ${largeArc} 1 ${end.x} ${end.y}`;
}

function buildGaugeDecorations(): {
  ticks: JSX.Element[];
  dots: JSX.Element[];
} {
  const tickCount = 40;
  const majorEvery = 5;
  const ticks: JSX.Element[] = [];
  const dots: JSX.Element[] = [];

  for (let i = 0; i <= tickCount; i += 1) {
    const angle = GAUGE_START_ANGLE + (i / tickCount) * GAUGE_SWEEP_DEG;
    if (i % majorEvery === 0) {
      const dot = pointOnGauge(angle, GAUGE_RADIUS + 18);
      dots.push(
        <circle
          key={`dot-${i}`}
          className="gauge-major-dot"
          cx={dot.x}
          cy={dot.y}
          r={2.2}
        />,
      );
    } else {
      const inner = pointOnGauge(angle, GAUGE_RADIUS - 22);
      const outer = pointOnGauge(angle, GAUGE_RADIUS - 14);
      ticks.push(
        <line
          key={`tick-${i}`}
          className="gauge-tick"
          x1={inner.x}
          y1={inner.y}
          x2={outer.x}
          y2={outer.y}
        />,
      );
    }
  }

  return { ticks, dots };
}

function CompletenessCard({ score }: { score: number }) {
  const animated = useAnimatedNumber(score);
  const { rating, bucket, colorVar } = getCompletenessRating(score);
  const clamped = Math.max(0, Math.min(100, animated));

  return (
    <div className="assessment-results-score-card">
      <p className="assessment-results-score-title">Completeness Score</p>
      <div
        className="radial-chart"
        style={{
          ['--ring-pct' as any]: clamped,
          ['--ring-color' as any]: colorVar,
          background: `conic-gradient(${colorVar} calc(var(--ring-pct, 0) * 1%), var(--kone-border) 0)`,
        }}
      >
        <div className="radial-chart-value">
          <span>{Math.round(clamped)}</span>
          <span>%</span>
        </div>
      </div>
      <span className={`assessment-results-score-status ${bucket}`}>
        {rating}
      </span>
      <p className="assessment-results-score-caption">
        Measures how complete the application's security documentation,
        requirements, traceability, controls, and evidence are.
      </p>
    </div>
  );
}

function RiskGaugeCard({ score }: { score: number }) {
  const animatedScore = useAnimatedNumber(score);
  const { label, colorVar } = getRiskLevel(score);
  const targetPct =
    (Math.max(0, Math.min(RISK_SCALE_MAX, score)) / RISK_SCALE_MAX) * 100;
  const animatedPct = useAnimatedNumber(targetPct);

  const decorations = useMemo(() => buildGaugeDecorations(), []);
  const handleAngle = GAUGE_START_ANGLE + (animatedPct / 100) * GAUGE_SWEEP_DEG;
  const handlePos = pointOnGauge(handleAngle);
  const trackPath = gaugeArcPath(
    GAUGE_START_ANGLE,
    GAUGE_START_ANGLE + GAUGE_SWEEP_DEG,
  );

  const displayValue = Number.isInteger(score)
    ? String(Math.round(animatedScore))
    : animatedScore.toFixed(1);

  return (
    <div className="assessment-results-score-card">
      <p className="assessment-results-score-title">Design Risk Score</p>
      <div className="gauge-chart">
        <svg className="gauge-svg" viewBox="0 0 170 170">
          <g className="gauge-decorations">
            {decorations.ticks}
            {decorations.dots}
          </g>
          <path className="gauge-track-path" d={trackPath} />
          <path
            className="gauge-value-path"
            pathLength={100}
            strokeDasharray={`${animatedPct} 100`}
            stroke={colorVar}
            d={trackPath}
          />
          <circle
            className="gauge-handle-glow"
            cx={handlePos.x}
            cy={handlePos.y}
            r={11}
            fill={colorVar}
          />
          <circle
            className="gauge-handle"
            cx={handlePos.x}
            cy={handlePos.y}
            r={7}
            fill={colorVar}
          />
        </svg>
        <div className="gauge-center-overlay">
          <div className="gauge-center-value">{displayValue}</div>
          <div className="gauge-center-label" style={{ color: colorVar }}>
            {label}
          </div>
        </div>
      </div>
      <p className="assessment-results-score-caption">
        Overall cybersecurity risk posture on the evaluation model's scale (0 =
        lowest risk, {RISK_SCALE_MAX} = highest risk).
      </p>
    </div>
  );
}

function metricBadgeClass(count: number | null | undefined): string {
  if (count === null || count === undefined) return 'badge-none';
  if (count > 3) return 'badge-many';
  if (count > 0) return 'badge-some';
  return 'badge-none';
}

function MetricCard({
  label,
  count,
}: {
  label: string;
  count: number | null | undefined;
}) {
  const displayValue =
    count === null || count === undefined ? '\u2013' : String(count);
  return (
    <div className="assessment-results-metric-card">
      <div className="assessment-results-metric-value">
        <span
          className={`assessment-results-metric-badge ${metricBadgeClass(
            count,
          )}`}
        />
        {displayValue}
      </div>
      <span className="assessment-results-metric-label">{label}</span>
    </div>
  );
}

export function SecuritySummaryAssessmentResults({
  report,
}: {
  report: Report | null | undefined;
}) {
  if (!hasEvaluationData(report)) {
    return (
      <p className="assessment-results-empty-state">
        No completed evaluation is selected, so a security summary assessment
        results is not available yet.
      </p>
    );
  }

  const completeness = report!.completeness_score as number;
  const risk = report!.system_risk_score as number;

  return (
    <div>
      <div className="assessment-results-scores">
        <CompletenessCard score={completeness} />
        <RiskGaugeCard score={risk} />
      </div>
      <div className="assessment-results-metrics">
        <MetricCard
          label="Validation Issues"
          count={report!.validation_issues_count}
        />
        <MetricCard
          label="Cross-source Gaps"
          count={report!.cross_source_gaps_count}
        />
        <MetricCard
          label="Traceability Gaps"
          count={report!.traceability_gaps_count}
        />
        <MetricCard
          label="Lifecycle Gaps"
          count={report!.lifecycle_gaps_count}
        />
      </div>
    </div>
  );
}
